import { Component, OnInit, inject, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { AnalyticsDataService, AnalyticsResponse } from '../../services/analytics-data.service';
import { forkJoin, Subject, Observable } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { GoogleMap } from '@angular/google-maps';
import { Firestore, collection, query, orderBy, getDocs, CollectionReference } from '@angular/fire/firestore';

@Component({
  selector: 'app-project-analytics',
  templateUrl: './project-analytics.component.html',
  styleUrls: ['./project-analytics.component.scss']
})
export class ProjectAnalyticsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(GoogleMap) map!: GoogleMap;
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private location: Location = inject(Location);
  private analyticsDataService: AnalyticsDataService = inject(AnalyticsDataService);
  private firestore: Firestore = inject(Firestore);
  private destroy$ = new Subject<void>();

  projectId: string | null = null;
  clientId: string | null = null;
  projectName: string = '';

  // Chart data and options
  userActivityData: any = null;
  userActivityOptions: any;
  
  activeUsersData: any = null;
  activeUsersOptions: any;
  
  appVersionData: any = null;
  appVersionOptions: any;
  
  
  
  countryData: any = null;
  countryOptions: any;
  
  deviceData: any = null;
  deviceOptions: any;
  
  // Full device data for sidebar
  allDevicesData: any[] = [];
  showDevicesSidebar: boolean = false;
  
  experienceLoadedData: any = null;
  experienceLoadedOptions: any;

  // CTA hierarchy data
  ctaHierarchyData: any[] = [];
  expandedExperiences: { [key: string]: boolean } = {};
  isLoadingCtaData: boolean = false;

  // Summary metrics
  totalActiveUsers: number = 0;
  totalAppLoads: number = 0;
  averageEngagementTime: string = '0m 0s';
  sessionsPerUser: number = 0;
  totalSessionDuration: string = '0s';
  averageSessionTime: string = '0m 0s';
  crashFreeRate: number = 0;

  // Engagement distribution data
  sessionEngagementDistribution = {
    highEngagement: 0,
    mediumEngagement: 0,
    lowEngagement: 0
  };

  // Session duration distribution data
  sessionDurationDistribution = {
    quickSessions: 0,
    shortSessions: 0,
    mediumSessions: 0,
    longSessions: 0
  };

  // Loading states
  isLoading: boolean = true;
  isLoadingCharts: boolean = true;
  isLoadingSummary: boolean = true;
  loadingError: string | null = null;
  hasData: boolean = false;

  // Google Maps Heatmap properties
  mapOptions: google.maps.MapOptions = {
    center: { lat: 51.5074, lng: -0.1278 }, // London center
    zoom: 11,
    mapTypeId: 'roadmap' as google.maps.MapTypeId,
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    fullscreenControl: true
  };
  
  heatmapData: google.maps.LatLng[] = [];
  heatmapLayer: google.maps.visualization.HeatmapLayer | null = null;
  isLoadingHeatmap: boolean = true;
  heatmapError: string | null = null;
  hasLocationData: boolean = false;

  // Getter for total location points count
  get totalLocationPoints(): number {
    return this.heatmapData ? this.heatmapData.length : 0;
  }

  // Valid experience names for filtering
  private validExperienceNames: Set<string> = new Set();

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('projectId');
    this.clientId = this.route.snapshot.paramMap.get('clientId');
    
    // Get project name from query params if available
    this.route.queryParams.subscribe(params => {
      this.projectName = params['name'] || 'Project';
    });

    this.initializeChartOptions();
    this.loadValidExperienceNames().then(() => {
      this.loadAnalyticsData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Wait for Google Maps API to be ready before initializing heatmap
    this.waitForGoogleMapsAPI().then(() => {
      // Only initialize heatmap if we have location data
      if (this.hasLocationData && this.heatmapData.length > 0) {
        // Set loading to false when we have data and can show the map
        this.isLoadingHeatmap = false;
        setTimeout(() => {
          this.initializeHeatmap();
        }, 500);
      } else {
        console.log('No location data available during ngAfterViewInit - keeping loading state until data arrives');
        // Keep loading state true until we have data to show
      }
    }).catch((error) => {
      console.error('Failed to load Google Maps API:', error);
      this.heatmapError = 'Failed to load Google Maps API. Please check your internet connection.';
      this.isLoadingHeatmap = false;
    });
  }

  /**
   * Wait for Google Maps API to be fully loaded
   */
  private waitForGoogleMapsAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if Google Maps API is already loaded
      if (typeof google !== 'undefined' && google.maps && google.maps.visualization) {
        resolve();
        return;
      }

      // Wait for the API to load with timeout
      let attempts = 0;
      const maxAttempts = 50; // 10 seconds max wait time
      
      const checkGoogleMaps = () => {
        attempts++;
        
        if (typeof google !== 'undefined' && google.maps && google.maps.visualization) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Google Maps API failed to load within timeout period'));
        } else {
          setTimeout(checkGoogleMaps, 200); // Check every 200ms
        }
      };
      
      checkGoogleMaps();
    });
  }

  loadAnalyticsData(): void {
    this.isLoading = true;
    this.isLoadingCharts = true;
    this.isLoadingSummary = true;
    this.loadingError = null;

    // Load all analytics data in parallel
    forkJoin({
      userActivity: this.analyticsDataService.getUserActivityOverTime(this.clientId, this.projectId),
      activeUsers: this.analyticsDataService.getActiveUsersRealTime(this.clientId, this.projectId),
      appVersions: this.analyticsDataService.getUsersByAppVersion(this.clientId, this.projectId),
      countries: this.analyticsDataService.getUsersByCountry(this.clientId, this.projectId),
      devices: this.analyticsDataService.getUsersByDevice(this.clientId, this.projectId),
      summaryMetrics: this.analyticsDataService.getSummaryMetrics(this.clientId, this.projectId),
      sessionEngagement: this.analyticsDataService.getSessionEngagementLevels(this.clientId, this.projectId),
      detailedSessionMetrics: this.analyticsDataService.getDetailedSessionMetrics(this.clientId, this.projectId),
      ctaData: this.analyticsDataService.getCtaAnalytics(this.clientId, this.projectId),
      experienceLoaded: this.analyticsDataService.getExperienceLoadedOverTime(this.clientId, this.projectId),
      locationData: this.analyticsDataService.getExperienceLocationData(this.clientId, this.projectId)
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoading = false;
        this.isLoadingCharts = false;
        this.isLoadingSummary = false;
      })
    ).subscribe({
      next: (data) => {
        this.processAnalyticsData(data);
        this.processCtaHierarchyData(data.ctaData as AnalyticsResponse);
        this.processExperienceLoadedData(data.experienceLoaded as AnalyticsResponse);
        this.processLocationData(data.locationData as AnalyticsResponse);
        this.hasData = true;
      },
      error: (error) => {
        console.error('Error loading analytics data:', error);
        this.loadingError = 'Failed to load analytics data. Please try again later.';
        this.hasData = false;
      }
    });
  }

  processAnalyticsData(data: any): void {
    // Process summary metrics - the data is in the rows array, not totals
    if (data.summaryMetrics?.rows && data.summaryMetrics.rows.length > 0) {
      const metrics = data.summaryMetrics.rows[0]; // First row contains the summary metrics
      
      // Total Active Users
      this.totalActiveUsers = metrics.activeUsers || 0;
      
      // Crash-free Rate (convert to percentage and round to 1 decimal)
      this.crashFreeRate = metrics.crashFreeUsersRate ? Number((metrics.crashFreeUsersRate * 100).toFixed(1)) : 0;
      
      // Average Session Duration
      if (metrics.averageSessionDuration) {
        this.averageSessionTime = this.analyticsDataService.formatDuration(metrics.averageSessionDuration);
      }
      
      // Sessions per User - use direct metric or calculate
      if (metrics.sessionsPerUser) {
        this.sessionsPerUser = Number(metrics.sessionsPerUser.toFixed(1));
      } else if (metrics.sessions && metrics.activeUsers) {
        this.sessionsPerUser = this.analyticsDataService.calculateSessionsPerUser(metrics.sessions, metrics.activeUsers);
      }
      
      // Total Session Duration - use only accurate user engagement duration from GA4
      if (metrics.userEngagementDuration) {
        this.totalSessionDuration = this.formatTotalDuration(metrics.userEngagementDuration);
      }
      
      // Average Engagement Time - calculate from total engagement duration
      if (metrics.userEngagementDuration && metrics.activeUsers) {
        this.averageEngagementTime = this.analyticsDataService.formatEngagementTime(
          metrics.userEngagementDuration,
          metrics.activeUsers
        );
      }
    }

    // Process session engagement and duration distributions
    if (data.sessionEngagement?.rows && data.sessionEngagement.rows.length > 0) {
      const sessionMetrics = data.sessionEngagement.rows[0];
      this.sessionEngagementDistribution = this.analyticsDataService.calculateSessionEngagementDistribution(sessionMetrics);
    }

    if (data.detailedSessionMetrics?.rows && data.detailedSessionMetrics.rows.length > 0) {
      // Combine metrics from all rows (new vs returning users)
      const combinedMetrics = data.detailedSessionMetrics.rows.reduce((acc: any, row: any) => {
        acc.sessions = (acc.sessions || 0) + (row.sessions || 0);
        acc.averageSessionDuration = Math.max(acc.averageSessionDuration || 0, row.averageSessionDuration || 0);
        acc.bounceRate = Math.max(acc.bounceRate || 0, row.bounceRate || 0);
        acc.engagementRate = Math.max(acc.engagementRate || 0, row.engagementRate || 0);
        return acc;
      }, {});
      
      this.sessionDurationDistribution = this.analyticsDataService.calculateSessionDurationDistribution(combinedMetrics);
    }

    // Process user activity data
    if (data.userActivity?.rows && data.userActivity.rows.length > 0) {
      this.processUserActivityData(data.userActivity);
    }

    // Process active users real-time data
    if (data.activeUsers?.rows && data.activeUsers.rows.length > 0) {
      this.processActiveUsersData(data.activeUsers);
    }

    // Process app versions data
    if (data.appVersions?.rows && data.appVersions.rows.length > 0) {
      this.processAppVersionsData(data.appVersions);
    }



    // Process countries data
    if (data.countries?.rows && data.countries.rows.length > 0) {
      this.processCountriesData(data.countries);
    }

    // Process devices data
    if (data.devices?.rows && data.devices.rows.length > 0) {
      this.processDevicesData(data.devices);
    }
  }

  /**
   * Filters out rows with invalid or unwanted dimension values
   * @param rows - Array of data rows to filter
   * @param dimensionExtractor - Function to extract the dimension value from a row
   * @returns Filtered array of rows
   */
  private filterValidDimensionRows(rows: any[], dimensionExtractor: (row: any) => string): any[] {
    return rows.filter(row => {
      const dimension = dimensionExtractor(row);
      return dimension &&
             dimension.trim() !== '' &&
             dimension.trim() !== '(not set)' &&
             dimension.toLowerCase() !== 'unknown';
    });
  }

  processUserActivityData(response: AnalyticsResponse): void {
    const labels = response.rows.map(row => {
      const date = this.analyticsDataService.parseGA4Date(row.date);
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    // Get the latest values for each metric (last data point)
    const latestRow = response.rows[response.rows.length - 1];
    const latest1Day = latestRow?.active1DayUsers || 0;
    const latest7Day = latestRow?.active7DayUsers || 0;
    const latest28Day = latestRow?.active28DayUsers || 0;

    // Calculate total app loads (sessions) over the 14-month period
    this.totalAppLoads = response.rows.reduce((total, row) => total + (row.sessions || 0), 0);

    this.userActivityData = {
      labels,
      datasets: [
        {
          label: `28 DAYS - ${latest28Day}`,
          data: response.rows.map(row => row.active28DayUsers || 0),
          borderColor: '#2096f3',
          backgroundColor: 'rgba(32, 150, 243, 0.1)',
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: `7 DAYS - ${latest7Day}`,
          data: response.rows.map(row => row.active7DayUsers || 0),
          borderColor: '#28a745',
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: `1 DAY - ${latest1Day}`,
          data: response.rows.map(row => row.active1DayUsers || 0),
          borderColor: '#fd7e14',
          backgroundColor: 'rgba(253, 126, 20, 0.1)',
          tension: 0.4,
          fill: false,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };
  }

  processActiveUsersData(response: AnalyticsResponse): void {
    // For real-time data, we'll simulate 30-minute intervals (this remains unchanged for real-time)
    const labels = Array.from({length: 30}, (_, i) => `${30-i}m ago`);
    const data = response.rows.slice(-30).map(row => row.activeUsers);
    
    // Pad with zeros if we don't have enough data
    while (data.length < 30) {
      data.unshift(0);
    }

    this.activeUsersData = {
      labels,
      datasets: [{
        label: 'Active Users',
        data,
        borderColor: '#28a745',
        backgroundColor: 'rgba(40, 167, 69, 0.1)',
        tension: 0.4,
        fill: true
      }]
    };
  }

  processAppVersionsData(response: AnalyticsResponse): void {
    // Filter out rows with empty, null, undefined, or "(not set)" app versions
    const filteredRows = this.filterValidDimensionRows(response.rows, row => row.platform || row.appVersion);

    // If no valid data after filtering, show empty chart
    if (filteredRows.length === 0) {
      this.appVersionData = {
        labels: ['No Data'],
        datasets: [{
          data: [100],
          backgroundColor: ['#6c757d'],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
      return;
    }

    const total = filteredRows.reduce((sum, row) => sum + row.activeUsers, 0);
    const labels = filteredRows.map(row => row.platform || row.appVersion || 'Unknown');
    const data = filteredRows.map(row => ((row.activeUsers / total) * 100).toFixed(1));

    this.appVersionData = {
      labels,
      datasets: [{
        data,
        backgroundColor: [
          '#2096f3', '#28a745', '#ffc107', '#fd7e14', '#dc3545', '#6c757d'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };

  }


  processCountriesData(response: AnalyticsResponse): void {
    // Filter out rows with empty, null, undefined, or "(not set)" countries
    const filteredRows = this.filterValidDimensionRows(response.rows, row => row.platform || row.country);

    // If no valid data after filtering, show empty chart
    if (filteredRows.length === 0) {
      this.countryData = {
        labels: ['No Data'],
        datasets: [{
          label: 'Active Users',
          data: [0],
          backgroundColor: '#6c757d',
          borderColor: '#6c757d',
          borderWidth: 1
        }]
      };
      return;
    }

    const labels = filteredRows.map(row => row.platform || row.country || 'Unknown');
    const data = filteredRows.map(row => row.activeUsers);

    this.countryData = {
      labels,
      datasets: [{
        label: 'Active Users',
        data,
        backgroundColor: '#2096f3',
        borderColor: '#2096f3',
        borderWidth: 1
      }]
    };
  }

  processDevicesData(response: AnalyticsResponse): void {
    // Filter out rows with empty, null, undefined, or "(not set)" device models
    const filteredRows = this.filterValidDimensionRows(response.rows, row => row.platform || row.deviceModel);

    // If no valid data after filtering, show empty chart
    if (filteredRows.length === 0) {
      this.deviceData = {
        labels: ['No Data'],
        datasets: [{
          data: [100],
          backgroundColor: ['#6c757d'],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };
      this.allDevicesData = [];
      return;
    }

    // Sort by active users descending
    const sortedRows = filteredRows.sort((a, b) => b.activeUsers - a.activeUsers);
    const total = sortedRows.reduce((sum, row) => sum + row.activeUsers, 0);

    // Store all devices data for sidebar
    this.allDevicesData = sortedRows.map((row, index) => ({
      rank: index + 1,
      deviceModel: row.platform || row.deviceModel || 'Unknown',
      activeUsers: row.activeUsers,
      percentage: ((row.activeUsers / total) * 100).toFixed(1)
    }));

    // Create top 10 for chart, group others as "Others"
    const top10Rows = sortedRows.slice(0, 10);
    const othersCount = sortedRows.slice(10).reduce((sum, row) => sum + row.activeUsers, 0);
    
    let chartLabels = top10Rows.map(row => row.platform || row.deviceModel || 'Unknown');
    let chartData = top10Rows.map(row => ((row.activeUsers / total) * 100).toFixed(1));
    
    // Add "Others" if there are more than 10 devices
    if (othersCount > 0) {
      chartLabels.push('Others');
      chartData.push(((othersCount / total) * 100).toFixed(1));
    }

    this.deviceData = {
      labels: chartLabels,
      datasets: [{
        data: chartData,
        backgroundColor: [
          '#2096f3', '#28a745', '#ffc107', '#fd7e14', '#dc3545', '#6c757d', '#17a2b8',
          '#6f42c1', '#e83e8c', '#20c997', '#adb5bd'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  }

  processExperienceLoadedData(response: AnalyticsResponse): void {
    if (!response?.rows || response.rows.length === 0) {
      this.experienceLoadedData = null;
      return;
    }

    // Filter data by eventName == "experience_loaded" and filter out invalid values
    const experienceLoadedRows = this.filterValidDimensionRows(
      response.rows.filter(row => row.eventName === 'experience_loaded'),
      row => row['customEvent:experience_name']
    );

    if (experienceLoadedRows.length === 0) {
      this.experienceLoadedData = null;
      return;
    }

    // Filter by valid experience names for this client/project
    const filteredRows = experienceLoadedRows.filter(row => {
      const experienceName = row['customEvent:experience_name'];
      return this.validExperienceNames.has(experienceName);
    });

    console.log(`Filtered experience loaded data: ${experienceLoadedRows.length} total rows, ${filteredRows.length} valid for this project`);

    if (filteredRows.length === 0) {
      this.experienceLoadedData = null;
      return;
    }

    // Group by experience name and date
    const experienceGroups: { [key: string]: { [key: string]: number } } = {};
    const allDates = new Set<string>();

    filteredRows.forEach(row => {
      const experienceName = row['customEvent:experience_name'];
      const date = this.analyticsDataService.parseGA4Date(row.date);
      const eventCount = row.eventCount || 0;

      if (!experienceGroups[experienceName]) {
        experienceGroups[experienceName] = {};
      }
      
      experienceGroups[experienceName][date] = (experienceGroups[experienceName][date] || 0) + eventCount;
      allDates.add(date);
    });

    // Sort dates
    const sortedDates = Array.from(allDates).sort();
    
    // Create labels for the chart
    const labels = sortedDates.map(date => {
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    // Create datasets for each experience
    const colors = [
      '#2096f3', '#28a745', '#ffc107', '#fd7e14', '#dc3545',
      '#6c757d', '#17a2b8', '#6f42c1', '#e83e8c', '#20c997'
    ];

    const datasets = Object.keys(experienceGroups).map((experienceName, index) => {
      const data = sortedDates.map(date => experienceGroups[experienceName][date] || 0);
      const color = colors[index % colors.length];
      
      return {
        label: experienceName,
        data,
        borderColor: color,
        backgroundColor: color + '20', // Add transparency
        tension: 0.4,
        fill: false,
        pointRadius: 4,
        pointHoverRadius: 6
      };
    });

    this.experienceLoadedData = {
      labels,
      datasets
    };
  }

  processLocationData(response: AnalyticsResponse): void {
    if (!response?.rows || response.rows.length === 0) {
      this.hasLocationData = false;
      this.heatmapData = [];
      this.isLoadingHeatmap = false;
      return;
    }

    // Filter data by eventName == "experience_opened_at" and filter out invalid values
    const locationRows = response.rows.filter(row => {
      const eventName = row.eventName;
      const latitude = row['customEvent:latitude'];
      const longitude = row['customEvent:longitude'];
      const experienceName = row['customEvent:experience_name'];
      
      return eventName === 'experience_opened_at' &&
             latitude && longitude &&
             latitude !== '(not set)' && longitude !== '(not set)' &&
             !isNaN(parseFloat(latitude)) && !isNaN(parseFloat(longitude)) &&
             parseFloat(latitude) !== 0 && parseFloat(longitude) !== 0;
             // Removed the experience name filter to allow valid location data even when experience_name is "(not set)"
    });

    if (locationRows.length === 0) {
      this.hasLocationData = false;
      this.heatmapData = [];
      this.isLoadingHeatmap = false;
      return;
    }

    // Convert to Google Maps LatLng objects
    this.heatmapData = locationRows.map(row => {
      const lat = parseFloat(row['customEvent:latitude']);
      const lng = parseFloat(row['customEvent:longitude']);
      return new google.maps.LatLng(lat, lng);
    });

    this.hasLocationData = true;
    this.isLoadingHeatmap = false; // Set loading to false now that we have data
    console.log('Processed location data:', this.heatmapData.length, 'points');
    
    // Initialize heatmap if Google Maps is ready and we now have data
    if (this.map && this.map.googleMap) {
      setTimeout(() => {
        this.initializeHeatmap();
      }, 100);
    }
  }

  initializeChartOptions(): void {
    // User activity options
    this.userActivityOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right',
          align: 'start',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            },
            generateLabels: (chart: any) => {
              const datasets = chart.data.datasets;
              return datasets.map((dataset: any, index: number) => {
                const meta = chart.getDatasetMeta(index);
                const style = meta.controller.getStyle(0, false);
                
                return {
                  text: dataset.label,
                  fillStyle: dataset.borderColor,
                  strokeStyle: dataset.borderColor,
                  lineWidth: 2,
                  pointStyle: 'circle',
                  hidden: !chart.isDatasetVisible(index),
                  index: index
                };
              });
            }
          }
        },
        title: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (tooltipItems: any[]) => {
              return tooltipItems[0].label;
            },
            label: (context: any) => {
              const label = context.dataset.label.split(' - ')[0];
              return `${label}: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: false
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      },
      elements: {
        point: {
          radius: 3,
          hoverRadius: 6
        },
        line: {
          borderWidth: 2
        }
      }
    };

    // Active users options
    this.activeUsersOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Active Users (Last 30 Minutes)'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Users'
          }
        },
        x: {
          display: false
        }
      }
    };

    // App version options
    this.appVersionOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right'
        },
        title: {
          display: true,
          text: 'Users by App Version (%)'
        }
      }
    };



    // Country options
    this.countryOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Active Users by Country'
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Active Users'
          }
        }
      }
    };

    // Device options
    this.deviceOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right'
        },
        title: {
          display: true,
          text: 'Active Users by Device Model (%)'
        }
      }
    };

    // Experience loaded options
    this.experienceLoadedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
          align: 'start',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            padding: 20,
            font: {
              size: 12,
              weight: 'bold'
            }
          }
        },
        title: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (tooltipItems: any[]) => {
              return tooltipItems[0].label;
            },
            label: (context: any) => {
              const experienceName = context.dataset.label;
              return `${experienceName}: ${context.parsed.y} loads`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Experience Loads'
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)'
          },
          ticks: {
            font: {
              size: 11
            }
          }
        },
        x: {
          title: {
            display: true,
            text: 'Date'
          },
          grid: {
            display: false
          },
          ticks: {
            font: {
              size: 11
            }
          }
        }
      },
      interaction: {
        mode: 'index',
        intersect: false
      },
      elements: {
        point: {
          radius: 3,
          hoverRadius: 6
        },
        line: {
          borderWidth: 2,
          tension: 0.4
        }
      }
    };
  }

  goBack(): void {
    this.location.back();
  }

  navigateToProject(): void {
    if (this.clientId && this.projectId) {
      this.router.navigate(['/', this.clientId, 'projects', this.projectId, 'experiences']);
    }
  }


  /**
   * Load valid experience names for the current client/project
   */
  private async loadValidExperienceNames(): Promise<void> {
    if (!this.clientId || !this.projectId) {
      console.warn('Cannot load experience names: Missing clientId or projectId');
      return;
    }

    try {
      const experiencesPath = `clients/${this.clientId}/projects/${this.projectId}/experiences`;
      const experiencesCollection = collection(this.firestore, experiencesPath);
      const experiencesQuery = query(experiencesCollection, orderBy('dateCreated', 'desc'));
      
      const querySnapshot = await getDocs(experiencesQuery);
      this.validExperienceNames.clear();
      
      querySnapshot.docs.forEach(doc => {
        const experienceData = doc.data();
        if (experienceData['name']) {
          this.validExperienceNames.add(experienceData['name']);
        }
      });

      console.log(`Loaded ${this.validExperienceNames.size} valid experience names for filtering:`, Array.from(this.validExperienceNames));
    } catch (error) {
      console.error('Error loading experience names:', error);
    }
  }

  /**
   * Process CTA hierarchy data for the expandable table
   */
  processCtaHierarchyData(response: AnalyticsResponse): void {
    if (!response?.rows || response.rows.length === 0) {
      this.ctaHierarchyData = [];
      return;
    }

    // Filter data by eventName == "cta_button_pressed" and filter out invalid values
    const ctaButtonPressedData = this.filterValidDimensionRows(
      response.rows.filter(row => row.eventName === 'cta_button_pressed'),
      row => row['customEvent:experience_name']
    ).filter(row => {
      const ctaName = row['customEvent:cta_name'];
      const experienceName = row['customEvent:experience_name'];
      return ctaName &&
             ctaName.trim() !== '' &&
             ctaName.trim() !== '(not set)' &&
             ctaName.toLowerCase() !== 'unknown' &&
             this.validExperienceNames.has(experienceName); // Filter by valid experience names
    });

    if (ctaButtonPressedData.length === 0) {
      this.ctaHierarchyData = [];
      return;
    }

    // Group by experience name
    const experienceGroups = ctaButtonPressedData.reduce((groups: any, row: any) => {
      const experienceName = row['customEvent:experience_name'];
      if (!groups[experienceName]) {
        groups[experienceName] = [];
      }
      groups[experienceName].push({
        ctaName: row['customEvent:cta_name'],
        presses: row.eventCount || 0
      });
      return groups;
    }, {});

    // Convert to hierarchical structure
    this.ctaHierarchyData = Object.keys(experienceGroups).map(experienceName => {
      const ctas = experienceGroups[experienceName];
      const totalPresses = ctas.reduce((sum: number, cta: any) => sum + cta.presses, 0);

      // Sort CTAs by count descending
      const sortedCtas = ctas
        .sort((a: any, b: any) => b.presses - a.presses)
        .map((cta: any) => ({
          ...cta,
          percentageOfExperience: totalPresses > 0 ? ((cta.presses / totalPresses) * 100).toFixed(1) : '0.0'
        }));

      return {
        experienceName,
        totalPresses,
        ctas: sortedCtas,
        expanded: false
      };
    }).sort((a, b) => b.totalPresses - a.totalPresses); // Sort experiences by total presses descending
  }

  /**
   * Toggle experience row expansion
   */
  toggleExperienceExpansion(experienceName: string): void {
    this.expandedExperiences[experienceName] = !this.expandedExperiences[experienceName];
  }

  /**
   * Check if experience is expanded
   */
  isExperienceExpanded(experienceName: string): boolean {
    return !!this.expandedExperiences[experienceName];
  }

  /**
   * Generate random coordinates around London for heatmap demo
   */
  generateRandomLondonCoordinates(): google.maps.LatLng[] {
    const londonCenter = { lat: 51.5074, lng: -0.1278 };
    const coordinates: google.maps.LatLng[] = [];
    
    // Generate 50-100 random points around London
    const numPoints = Math.floor(Math.random() * 50) + 50;
    
    for (let i = 0; i < numPoints; i++) {
      // Generate random offset within ~20km radius of London center
      const offsetLat = (Math.random() - 0.5) * 0.3; // ~0.15 degrees = ~16km
      const offsetLng = (Math.random() - 0.5) * 0.4; // ~0.2 degrees = ~14km
      
      const lat = londonCenter.lat + offsetLat;
      const lng = londonCenter.lng + offsetLng;
      
      coordinates.push(new google.maps.LatLng(lat, lng));
    }
    
    return coordinates;
  }

  /**
   * Initialize Google Maps heatmap
   */
  initializeHeatmap(): void {
    // First check if we have location data - don't initialize if no data
    if (!this.hasLocationData || this.heatmapData.length === 0) {
      console.log('No location data available for heatmap - skipping initialization');
      this.heatmapError = null; // Clear any previous errors
      return;
    }

    // Wait for the map component to be ready
    if (!this.map) {
      console.warn('Map component not ready, retrying...');
      setTimeout(() => this.initializeHeatmap(), 500);
      return;
    }

    // Wait for the Google Map instance to be ready
    if (!this.map.googleMap) {
      console.warn('Google Map instance not ready, retrying...');
      setTimeout(() => this.initializeHeatmap(), 500);
      return;
    }

    try {
      console.log('Initializing heatmap with', this.heatmapData.length, 'data points...');
      
      // Verify Google Maps visualization library is available
      if (!google.maps.visualization || !google.maps.visualization.HeatmapLayer) {
        throw new Error('Google Maps visualization library not loaded');
      }
      
      // Create bounds to encompass all data points
      const bounds = new google.maps.LatLngBounds();
      this.heatmapData.forEach(point => {
        bounds.extend(point);
      });
      
      // Fit the map to show all points with some padding
      this.map.googleMap.fitBounds(bounds);
      
      // Add some padding around the bounds for better visualization
      const padding = {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      };
      this.map.googleMap.fitBounds(bounds, padding);
      
      // Create heatmap layer with real data
      this.heatmapLayer = new google.maps.visualization.HeatmapLayer({
        data: this.heatmapData,
        map: this.map.googleMap
      });

      // Configure heatmap options
      this.heatmapLayer.setOptions({
        radius: 20,
        opacity: 0.8,
        gradient: [
          'rgba(0, 255, 255, 0)',
          'rgba(0, 255, 255, 1)',
          'rgba(0, 191, 255, 1)',
          'rgba(0, 127, 255, 1)',
          'rgba(0, 63, 255, 1)',
          'rgba(0, 0, 255, 1)',
          'rgba(0, 0, 223, 1)',
          'rgba(0, 0, 191, 1)',
          'rgba(0, 0, 159, 1)',
          'rgba(0, 0, 127, 1)',
          'rgba(63, 0, 91, 1)',
          'rgba(127, 0, 63, 1)',
          'rgba(191, 0, 31, 1)',
          'rgba(255, 0, 0, 1)'
        ]
      });

      console.log('Heatmap initialized successfully with', this.heatmapData.length, 'real data points');
      
    } catch (error) {
      console.error('Error initializing heatmap:', error);
      this.heatmapError = 'Failed to initialize heatmap: ' + error;
    }
  }

  /**
   * Calculate the center point of the map based on data points
   */
  calculateMapCenter(dataPoints: google.maps.LatLng[]): google.maps.LatLng | null {
    if (!dataPoints || dataPoints.length === 0) {
      return null;
    }

    let totalLat = 0;
    let totalLng = 0;

    dataPoints.forEach(point => {
      totalLat += point.lat();
      totalLng += point.lng();
    });

    const avgLat = totalLat / dataPoints.length;
    const avgLng = totalLng / dataPoints.length;

    return new google.maps.LatLng(avgLat, avgLng);
  }

  /**
   * Calculate optimal zoom level based on data spread
   */
  calculateOptimalZoom(dataPoints: google.maps.LatLng[]): number {
    if (!dataPoints || dataPoints.length === 0) {
      return 11; // Default zoom
    }

    if (dataPoints.length === 1) {
      return 15; // Close zoom for single point
    }

    // Calculate bounds
    let minLat = dataPoints[0].lat();
    let maxLat = dataPoints[0].lat();
    let minLng = dataPoints[0].lng();
    let maxLng = dataPoints[0].lng();

    dataPoints.forEach(point => {
      const lat = point.lat();
      const lng = point.lng();
      
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
    });

    // Calculate span
    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    const maxSpan = Math.max(latSpan, lngSpan);

    // Determine zoom level based on span
    if (maxSpan > 10) return 3;
    if (maxSpan > 5) return 5;
    if (maxSpan > 2) return 7;
    if (maxSpan > 1) return 9;
    if (maxSpan > 0.5) return 11;
    if (maxSpan > 0.1) return 13;
    return 15;
  }

  /**
   * Refresh heatmap with new data
   */
  refreshHeatmapData(): void {
    if (this.heatmapLayer && this.hasLocationData && this.heatmapData.length > 0) {
      try {
        // Update heatmap layer with current data
        this.heatmapLayer.setData(this.heatmapData);
        
        console.log('Heatmap refreshed with', this.heatmapData.length, 'data points');
      } catch (error) {
        console.error('Error refreshing heatmap:', error);
        this.heatmapError = 'Failed to refresh heatmap data';
      }
    } else {
      console.warn('No location data available for heatmap refresh');
    }
  }

  /**
   * Handle map ready event
   */
  onMapReady(): void {
    console.log('Map is ready');
    if (this.hasLocationData && this.heatmapData.length > 0) {
      console.log('Initializing heatmap...');
      setTimeout(() => {
        this.initializeHeatmap();
      }, 100);
    } else {
      console.log('No location data available - skipping heatmap initialization');
    }
  }

  /**
   * Format total duration in seconds to appropriate time unit (seconds, minutes, or hours)
   */
  formatTotalDuration(totalSeconds: number): string {
    if (!totalSeconds || totalSeconds === 0) {
      return '0s';
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    // Show hours if >= 1 hour
    if (hours >= 1) {
      if (minutes > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${hours}h`;
    }
    
    // Show minutes if >= 1 minute
    if (minutes >= 1) {
      if (seconds > 0) {
        return `${minutes}m ${seconds}s`;
      }
      return `${minutes}m`;
    }
    
    // Show seconds only
    return `${seconds}s`;
  }

  /**
   * Show devices sidebar
   */
  showAllDevices(): void {
    this.showDevicesSidebar = true;
  }

  /**
   * Hide devices sidebar
   */
  hideDevicesSidebar(): void {
    this.showDevicesSidebar = false;
  }
}